/**
 * Google Maps のキャンバス（Leafletの MapContainer 相当）
 *
 * APIProvider をラップしているため、地図を使う画面でこれを置くだけでよい。
 * APIのスクリプト読み込みは @vis.gl 側でシングルトン管理されるので、
 * 複数箇所にマウントしても二重ロードにはならない。
 */

import { APIProvider, Map, type MapMouseEvent } from '@vis.gl/react-google-maps'
import { type CSSProperties, type ReactNode, useEffect, useRef } from 'react'

import { sendMapLoadBeacon } from '@/api/services/api-usage'

import {
  DEFAULT_MAP_CENTER,
  GOOGLE_MAPS_API_KEY,
  GOOGLE_MAPS_MAP_ID,
  isGoogleMapsConfigured,
} from './google-maps-config'
import { MapErrorBoundary } from './map-error-boundary'
import { MapTypeControl } from './map-type-control'
import { type MapTypeId, useMapType } from './use-map-type'

/** Advanced Marker を使うため marker ライブラリを常時ロードする */
const LIBRARIES = ['marker']

type GoogleMapCanvasProps = {
  /** 初期中心。以降のパン/ズームはユーザー操作に委ねる（非制御） */
  defaultCenter?: google.maps.LatLngLiteral
  defaultZoom?: number
  className?: string
  style?: CSSProperties
  /** 'greedy' は1本指ドラッグでパン可（Leafletの既定挙動に近い） */
  gestureHandling?: 'greedy' | 'cooperative' | 'none' | 'auto'
  zoomControl?: boolean
  disableDefaultUI?: boolean
  /** 地図/航空写真の切替ボタンを表示する（選択は localStorage に保存される） */
  mapTypeControl?: boolean
  defaultMapTypeId?: MapTypeId
  onClick?: (event: MapMouseEvent) => void
  children?: ReactNode
}

export function GoogleMapCanvas({
  defaultCenter = DEFAULT_MAP_CENTER,
  defaultZoom = 14,
  className,
  style,
  gestureHandling = 'greedy',
  zoomControl = true,
  disableDefaultUI = true,
  mapTypeControl = false,
  defaultMapTypeId = 'roadmap',
  onClick,
  children,
}: GoogleMapCanvasProps) {
  const [mapTypeId, setMapTypeId] = useMapType(defaultMapTypeId)

  // 地図の表示はバックエンドを通らないので、ここで消費量を知らせる。
  // StrictMode の二重実行で水増ししないよう ref で一度だけに限る。
  const beaconSent = useRef(false)
  useEffect(() => {
    if (beaconSent.current) return
    if (!isGoogleMapsConfigured()) return
    beaconSent.current = true
    sendMapLoadBeacon()
  }, [])

  if (!isGoogleMapsConfigured()) {
    return (
      <div className={className} style={style}>
        <div className="text-muted-foreground bg-muted flex h-full w-full items-center justify-center p-4 text-center text-sm">
          Google Maps のAPIキーが設定されていません
        </div>
      </div>
    )
  }

  return (
    <MapErrorBoundary className={className} style={style}>
      <APIProvider apiKey={GOOGLE_MAPS_API_KEY} libraries={LIBRARIES}>
        <Map
          mapId={GOOGLE_MAPS_MAP_ID}
          defaultCenter={defaultCenter}
          defaultZoom={defaultZoom}
          mapTypeId={mapTypeId}
          gestureHandling={gestureHandling}
          zoomControl={zoomControl}
          disableDefaultUI={disableDefaultUI}
          clickableIcons={false}
          reuseMaps
          className={className}
          style={style}
          onClick={onClick}
        >
          {mapTypeControl && <MapTypeControl value={mapTypeId} onChange={setMapTypeId} />}
          {children}
        </Map>
      </APIProvider>
    </MapErrorBoundary>
  )
}
